import type {
  ApplicationSnapshot,
  ApplicationProductMetrics,
  ProductMetricSnapshot,
  ProductStatsAudience,
} from './types.js'
import { PRODUCT_STATS_EXCLUDED_ACTORS } from './types.js'

export interface TractionScore {
  score: number | null
  coverage: number
  reach: number | null
  momentum: number | null
  engagement: number | null
  liveActivity: number | null
  status: 'ready' | 'building-baseline' | 'stale' | 'not-instrumented' | 'audience-unconfirmed'
}

/** Checks the producer's declaration. This cannot detect an unmarked staff browser. */
export function declaresExternalAudience(audience: ProductStatsAudience | undefined) {
  return audience?.policyVersion === '1.0' &&
    audience.scope === 'external-only' &&
    Array.isArray(audience.excludedActors) &&
    PRODUCT_STATS_EXCLUDED_ACTORS.every((actor) => audience.excludedActors.includes(actor))
}

export function audiencePolicyKey(audience: ProductStatsAudience | undefined) {
  return audience ? JSON.stringify([
    audience.policyVersion, audience.scope, [...audience.excludedActors].sort(),
    audience.method, audience.coverageStart,
  ]) : ''
}

/** Freshness expires on reads too; a cached status flag cannot extend a sample's life. */
export function productStatsAreFresh(source: ApplicationProductMetrics | undefined, now = Date.now()) {
  if (source?.sourceStatus !== 'fresh') return false
  const observedAt = Date.parse(source.observedAt ?? '')
  const expiresAt = source.expiresAt === undefined
    ? observedAt + 900_000 // Conservative compatibility for snapshots from before explicit expiry.
    : Date.parse(source.expiresAt)
  return Number.isFinite(observedAt) && Number.isFinite(expiresAt) &&
    expiresAt > observedAt && now < expiresAt
}

export function availableExternalMetric(
  application: ApplicationSnapshot,
  semantic: ProductMetricSnapshot['semantic'],
  period?: ProductMetricSnapshot['period'],
  now = Date.now(),
) {
  const source = application.productMetrics
  if (!source || !productStatsAreFresh(source, now) || !declaresExternalAudience(source.audience)) {
    return undefined
  }
  const metric = availableMetric(application, semantic, period)
  if (!metric) return undefined
  const observedAt = Date.parse(source.observedAt ?? '')
  const coverageStart = Date.parse(source.audience!.coverageStart)
  if (!Number.isFinite(observedAt) || !Number.isFinite(coverageStart) || coverageStart > observedAt) {
    return undefined
  }
  const days = { '24h': 1, '7d': 7, '30d': 30 }[metric.period as '24h' | '7d' | '30d']
  // A partial first day or week is unknown for comparison, not a full zero-filled window.
  if (days && coverageStart > observedAt - days * 86_400_000) return undefined
  return metric
}

export function availableMetric(
  application: ApplicationSnapshot,
  semantic: ProductMetricSnapshot['semantic'],
  period?: ProductMetricSnapshot['period'],
) {
  return application.productMetrics?.metrics.find(
    (metric) =>
      metric.status === 'available' &&
      metric.value !== undefined &&
      metric.semantic === semantic &&
      (period === undefined || metric.period === period),
  )
}

function percentile(value: number, values: number[]) {
  if (!values.length) return null
  const sorted = [...values].sort((left, right) => left - right)
  if (sorted.length === 1) return 100
  const below = sorted.filter((candidate) => candidate < value).length
  const equal = sorted.filter((candidate) => candidate === value).length
  return Math.round(((below + Math.max(0, equal - 1) / 2) / (sorted.length - 1)) * 100)
}

function mean(values: Array<number | null>) {
  const available = values.filter((value): value is number => value !== null)
  return available.length
    ? available.reduce((sum, value) => sum + value, 0) / available.length
    : null
}

export function tractionScores(applications: ApplicationSnapshot[], now = Date.now()) {
  const externalMetric = (
    application: ApplicationSnapshot,
    semantic: ProductMetricSnapshot['semantic'],
    period?: ProductMetricSnapshot['period'],
  ) => availableExternalMetric(application, semantic, period, now)
  const metricValue = (
    application: ApplicationSnapshot,
    semantic: ProductMetricSnapshot['semantic'],
    period?: ProductMetricSnapshot['period'],
  ) => externalMetric(application, semantic, period)?.value
  const fresh = applications.filter(
    (application) => productStatsAreFresh(application.productMetrics, now) &&
      declaresExternalAudience(application.productMetrics?.audience),
  )
  const reachInputs = fresh.flatMap((application) => {
    const active = metricValue(application, 'active-users', '7d')
    const total = metricValue(application, 'total-users', 'all-time')
    const value = mean([
      active === undefined ? null : Math.log1p(active),
      total === undefined ? null : Math.log1p(total),
    ])
    return value === null ? [] : [value]
  })
  const momentumInputs = fresh.flatMap((application) => {
    const active = externalMetric(application, 'active-users', '7d')
    const newcomers = externalMetric(application, 'new-users', '7d')
    const value = mean([
      active && (active.value ?? 0) >= 5 ? active.change7d?.percent ?? null : null,
      newcomers && (newcomers.value ?? 0) >= 5
        ? newcomers.change7d?.percent ?? null
        : null,
    ])
    return value === null ? [] : [value]
  })
  const engagementInputs = fresh.flatMap((application) => {
    const active = metricValue(application, 'active-users', '7d')
    const total = metricValue(application, 'total-users', 'all-time')
    const core = metricValue(application, 'core-action', '7d')
    const value = mean([
      active !== undefined && total ? Math.min(100, (active / total) * 100) : null,
      active && core !== undefined ? Math.log1p(core / active) : null,
    ])
    return value === null ? [] : [value]
  })
  const liveInputs = fresh.flatMap((application) => {
    const online = metricValue(application, 'online-users', 'instant')
    const core = metricValue(application, 'core-action', '24h')
    const value = mean([
      online === undefined ? null : Math.log1p(online),
      core === undefined ? null : Math.log1p(core),
    ])
    return value === null ? [] : [value]
  })

  return new Map(
    applications.map((application) => {
      if (!application.productMetrics) {
        return [
          application.application.id,
          emptyScore('not-instrumented'),
        ] as const
      }
      if (!productStatsAreFresh(application.productMetrics, now)) {
        return [application.application.id, emptyScore('stale')] as const
      }
      if (!declaresExternalAudience(application.productMetrics.audience)) {
        return [application.application.id, emptyScore('audience-unconfirmed')] as const
      }
      const active = metricValue(application, 'active-users', '7d')
      const total = metricValue(application, 'total-users', 'all-time')
      const core7d = metricValue(application, 'core-action', '7d')
      const online = metricValue(application, 'online-users', 'instant')
      const core24h = metricValue(application, 'core-action', '24h')
      const activeMetric = externalMetric(application, 'active-users', '7d')
      const newMetric = externalMetric(application, 'new-users', '7d')
      const reachValue = mean([
        active === undefined ? null : Math.log1p(active),
        total === undefined ? null : Math.log1p(total),
      ])
      const momentumValue = mean([
        activeMetric && (activeMetric.value ?? 0) >= 5
          ? activeMetric.change7d?.percent ?? null
          : null,
        newMetric && (newMetric.value ?? 0) >= 5
          ? newMetric.change7d?.percent ?? null
          : null,
      ])
      const engagementValue = mean([
        active !== undefined && total ? Math.min(100, (active / total) * 100) : null,
        active && core7d !== undefined ? Math.log1p(core7d / active) : null,
      ])
      const liveValue = mean([
        online === undefined ? null : Math.log1p(online),
        core24h === undefined ? null : Math.log1p(core24h),
      ])
      const dimensions = {
        reach: reachValue === null ? null : percentile(reachValue, reachInputs),
        momentum:
          momentumValue === null ? null : percentile(momentumValue, momentumInputs),
        engagement:
          engagementValue === null
            ? null
            : percentile(engagementValue, engagementInputs),
        liveActivity:
          liveValue === null ? null : percentile(liveValue, liveInputs),
      }
      const weighted: Array<[number | null, number]> = [
        [dimensions.reach, 0.35],
        [dimensions.momentum, 0.35],
        [dimensions.engagement, 0.2],
        [dimensions.liveActivity, 0.1],
      ]
      const present = weighted.filter(
        (entry): entry is [number, number] => entry[0] !== null,
      )
      const coverage = present.length / weighted.length
      const weight = present.reduce((sum, entry) => sum + entry[1], 0)
      const raw = present.reduce((sum, [value, dimensionWeight]) =>
        sum + value * dimensionWeight, 0)
      const score = present.length >= 2 && weight
        ? Math.round((raw / weight) * (0.5 + coverage / 2))
        : null
      return [
        application.application.id,
        {
          ...dimensions,
          coverage,
          score,
          status: score === null ? 'building-baseline' : 'ready',
        } satisfies TractionScore,
      ] as const
    }),
  )
}

function emptyScore(
  status: TractionScore['status'],
): TractionScore {
  return {
    score: null,
    coverage: 0,
    reach: null,
    momentum: null,
    engagement: null,
    liveActivity: null,
    status,
  }
}
